(ns story-builder.spec.story
  (:require [clojure.spec.alpha :as s]
            [clojure.string :as str]
            [story-builder.util.url :as util.url]))

(def uuid-regex #"^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$")

(def ussd-regex #"^tel:\*[0-9\*#]*[0-9]+[0-9\*#]*\#$")

(def layer-order
  ["bg"
   "img"
   "reveal"
   "scratch"
   "shape"
   "video"
   "slideshow"
   "overlay"
   "branding"
   "profile-pic"
   "ad"
   "replay-button"
   "author"
   "username"
   "date"
   "category"
   "title"
   "subheading"
   "text"
   "captions"
   "rich-text"
   "message"
   "source"
   "regular-price"
   "price"
   "question"
   "answer"
   "embed"
   "tag"
   "CTA"
   "attachment"
   "audio"
   "product-tag"
   "slider"
   "quiz"
   "image-quiz"
   "poll"
   "binary-poll"
   "image-poll"
   "quiz-results"
   "poll-results"])

(def static-layers #{"branding" "attachment"})

(def layer-type->order
  (into {}
        (map-indexed (fn [index layer-type] [layer-type index]))
        layer-order))

(defn layer->order [layer]
  (-> layer :type layer-type->order))

(defn sort-layers [layers]
  (vec (sort-by layer->order layers)))

(s/def ::accent-color string?)
(s/def ::alignment string?)
(s/def :amp/theme string?)
(s/def ::auto-advance boolean?)
(s/def ::autoplay boolean?)
(s/def ::background (s/or :map map? :string string?))
(s/def ::background-audio map?)
(s/def ::branding-id (s/or :string string? :nil nil?))
(s/def ::captions-layer-id string?)
(s/def ::chip-style string?)
(s/def ::class-name string?)
(s/def ::class (s/coll-of ::class-name :kind vector?))
(s/def ::click (s/coll-of ::url :kind vector?))
(s/def ::caption (s/or :string string? :nil nil?))
(s/def ::content (s/or :string string? :nil nil? :vector vector?))
;; FIXME: change the content spec from vector to rich-text
;; (s/def ::content (s/or :string string? :nil nil? :rich-text-content ::rich-text.content/content))
(s/def ::content-enum keyword?)
(s/def ::correct boolean?)
(s/def ::description (s/or :string string? :nil nil?))
(s/def ::duration number?)
(s/def :editor/name string?)
(s/def ::enabled boolean?)
(s/def ::event-name string?)
(s/def ::font-id string?)
(s/def ::framed string?)
(s/def ::full-bleed boolean?)
(s/def ::fullscreen boolean?)
(s/def ::id (s/or :string string? :int int?))
(s/def ::is-background boolean?)
(s/def ::intro-guide boolean?)
(s/def ::imp (s/coll-of ::url :kind vector?))
(s/def ::label string?)
(s/def ::landscape boolean?)
(s/def :layer/id string?)
(s/def :layer/align-relative-to #{:responsive :full})
(s/def ::style map?)
(s/def ::layer-style map?)
(s/def ::content-style map?)
(s/def ::quality (s/or :auto #(= % "auto") :number #(and (number? %) (<= 0 % 100))))
(s/def ::layer-classes (s/or :map map? :nil nil?))
(s/def ::layout string?)
(s/def ::loop boolean?)
(s/def ::media-id string?)
(s/def ::muted boolean?)
(s/def ::name string?)
(s/def :option/confetti string?)
(s/def ::placement-id (s/or :tempid record? :id int?))
(s/def ::products map?)
(s/def ::stories vector?)
(s/def ::replay boolean?)
(s/def :scene/id string?)
(s/def ::slideshow-id string?)
(s/def ::src (s/or :string (s/and string? (s/or :url #(re-matches util.url/url-regex %) :svg #(str/starts-with? % "<svg"))) :blank str/blank?))
(s/def :story/id int?)
(s/def ::idx int?)
(s/def ::size string?)
(s/def :story/property-id int?)
(s/def :story/publisher-id int?)
(s/def :story/publisher int?)
(s/def :story/loop-indefinitely? boolean?)
(s/def :story/loop-iterations number?)
(s/def ::text string?)
(s/def ::text-color string?)
(s/def ::theme string?)
(s/def ::threshold int?)
(s/def ::type string?)
(s/def ::target string?)
(s/def ::text-vertical-align keyword?)
(s/def ::transform-id string?)
(s/def :action/url (s/or :empty str/blank? :url ::url :url ::ussd-quick-code))
(s/def ::url (s/and string? #(re-matches util.url/url-regex %)))
(s/def ::video-layer-id string?)
(s/def ::ussd-quick-code (s/and string? #(re-matches ussd-regex %)))
(s/def ::width (s/or :int int? :nil nil?))
(s/def ::height (s/or :int int? :nil nil?))

(s/def ::reveal map?)

(s/def ::animation
  (s/and string?
         #{"drop"
           "fade-in"
           "fade-out"
           "fly-in-top"
           "fly-in-bottom"
           "fly-in-left"
           "fly-in-right"
           "pulse"
           "rotate"
           "rotate-in-left"
           "rotate-in-right"
           "scale-fade-down"
           "scale-fade-up"
           "twirl-in"
           "twirl-in-clockwise"
           "twirl-in-counterclockwise"
           "whoosh-in-left"
           "whoosh-in-right"
           "pan-left"
           "pan-right"
           "pan-down"
           "pan-up"
           "zoom-in"
           "zoom-out"}))

(s/def ::animate-after ::id)
(s/def ::animate-delay number?)

(s/def :animation/delay number?)
(s/def :animation/direction keyword?)
(s/def :animation/duration number?)
(s/def :animation/timing-function string?)
(s/def :animation/iterations number?)
(s/def :animation/loop-indefinitely? boolean?)
(s/def :animation/start-position #{:scene :layer :custom})
(s/def :animation/trigger #{:auto :hover :click :scene-interaction :page-scroll})
(s/def :animation/animate-after ::id)
;; Rich-text animation split-by
(s/def :animation/split-by #{:whole-layer :letter :word :sentence})

(s/def ::animation-new
  (s/keys :opt-un [::animation
                   :animation/delay
                   :animation/direction
                   :animation/duration
                   :animation/timing-function
                   :animation/iterations
                   :animation/loop-indefinitely?
                   :animation/start-position
                   :animation/trigger
                   :animation/animate-after
                   :animation/split-by]))

(s/def ::animations
  (s/coll-of ::animation-new :kind vector?))

(s/def :action/scene (s/or :id :scene/id
                           :idx ::idx
                           :name ::name))

(s/def ::tracker
  (s/keys :req-un [::type
                   ::url]))

(s/def ::trackers (s/coll-of ::tracker :kind vector?))

(s/def :action/amp
  (s/keys :opt-un [:amp/theme]))

(s/def ::action
  (s/keys :opt-un [:action/amp
                   :action/url
                   :action/scene
                   ::target
                   ::trackers]))

(s/def :layer/type (s/and ::type (set layer-order)))

(s/def ::builder
  (s/keys :opt-un [::is-hidden
                   ::is-locked]))

(def scene-types
  #{"ad"
    "cover"
    "embed"
    "exit"
    "quiz"
    "quiz-results"
    "binary-poll"
    "poll"
    "poll-results"
    "product"
    "social-card"
    "shoppable"
    "standard"
    "tag"})

(s/def :scene/type
  (s/and ::type scene-types))

(s/def ::element
  (s/keys :req-un [::name
                   ::content]
          :opt-un [::style]))

(s/def ::elements (s/coll-of ::element :kind vector?))

(s/def ::slideshow-item
  (s/keys :req-un [::id]
          :opt-un [::src
                   ::url]))

(s/def ::items (s/coll-of ::slideshow-item :kind vector?))

;; (s/def ::source
;;   (s/keys :opt-un [::id
;;                    ::name]))

(s/def ::prompt
  (s/keys :opt-un [::alignment
                   ::background
                   ::size
                   ::text
                   ::text-color]))

(s/def ::category
  (s/keys :opt-un [::title
                   ::description]))

(s/def ::categories
  (s/coll-of ::category :kind vector?))

(s/def :option/results
  (s/keys :opt-un [::category
                   ::threshold]))

(s/def ::option
  (s/keys :opt-un [:option/results
                   :option/confetti
                   :option/text]))

(s/def ::options
  (s/coll-of ::option))

(s/def :layer/amp
  (s/keys :opt-un [::accent-color
                   ::prompt
                   ::options
                   ::option]))

(s/def :poll.answer/jump-to-next? boolean?)

(s/def :poll.answer/scene
  (s/or :id :scene/id
        :idx ::idx
        :name ::name))

(s/def :poll/incomplete-categories
  (fn [layer]
    (if (contains? #{"poll" "image-poll"} (:type layer))
      (let [options (get-in layer [:amp :options] [])]
        (or (every? #(not (str/blank? (:category %))) options)
            (every? #(str/blank? (:category %)) options)))
      true)))

(s/def ::slideshow
  (s/keys :opt-un [::items]))

(s/def ::layer
  (s/and
   (s/keys :req-un [:layer/id
                    :layer/type]
           :opt-un [::animation ; Deprecated, we now use ::animations.
                    ::animations
                    ::animate-after ; Deprecated at this level. Should be on each individual animation.
                    ::captions-layer-id
                    ::class
                    ::animate-delay
                    ::width
                    ::height
                    ::branding-id
                    ::font-id
                    ::builder
                    ::caption
                    ::content
                    ::content-enum
                    ::correct
                    ::description
                    ::elements
                    ::is-background
                    ;; ::style
                    ::layer-style
                    ;; ::content-style
                    ::quality
                    ::layer-classes
                    ::loop
                    ::source
                    ::media-id
                    ::muted
                    ::placement-id
                    ::src
                    ::effect
                    ::items
                    ::slideshow-id
                    ::slideshow
                    ::text-vertical-align
                    ::transform-id
                    ::video-layer-id
                    :layer/amp
                    ::reveal
                    ::scratch]
           :opt [:layer/name
                 :layer/align-relative-to
                 :poll.answer/jump-to-next?
                 :poll.answer/scene
                 :shape/type ;; present when the layer is a generated shape
                 :shape/stroke-color
                 :shape/stroke-width
                 :shape/stroke-linejoin
                 :shape/fill-color
                 :shape/corner-count
                 :shape/angle-offset
                 :shape/middle-point?
                 :shape/middle-point-distance
                 :shape/corner-radius
                 :shape/segment-width
                 ;;:shape/points
                 ;;:shape/closed-path?
                 :shape.decorator/start
                 :shape.decorator/end])
   #(if (= (:type %) "video")
      (or (contains? % :poster)
          (contains? % :source-url)) ; Video should have poster unless still processing.
      true)
   :poll/incomplete-categories))

(defn sorted-by?
  ([keyfn] (sorted-by? keyfn <=))
  ([keyfn comparator] (fn [coll]
                        (->> coll
                             (map keyfn)
                             (partition 2 1)
                             (every? (fn [[key1 key2]] (comparator key1 key2)))))))

(comment
  ;; => [true true false true true]
  (mapv (sorted-by? layer->order)
        [[{:type "bg"} {:type "bg"}]
         [{:type "bg"} {:type "img"}]
         [{:type "img"} {:type "bg"}]
         [{:type "bg"}]
         []]))

(s/def ::layers (s/and (s/coll-of ::layer :kind vector?)
                       (sorted-by? layer->order)))

(s/def ::bg
  (s/keys :opt-un [::src]))

(s/def :interactive/animation
  (s/keys :opt-un [::delay
                   ::duration
                   ::timing-function
                   ::type]))

(s/def ::interactive
  (s/keys :opt-un [::theme
                   ::accent-color
                   ::chip-style
                   ::prompt
                   ::categories
                   :interactive/animation
                   ::animate-after
                   ::animate-delay]))

(s/def :scene/amp
  (s/keys :opt-un [::interactive]))

(s/def :progress-bar/fill-color string?)
(s/def :progress-bar/bar-color string?)
(s/def :progress-bar/thickness number?)

(s/def :scene/progress-bar
  (s/keys :opt-un [:progress-bar/fill-color
                   :progress-bar/bar-color
                   :progress-bar/thickness]))

(s/def :poll/results? boolean?)

(s/def ::scene
  (s/keys :req-un [::layers
                   ::layout
                   :scene/id
                   :scene/type]
          :opt-un [::action
                   ::auto-advance
                   ::bg
                   ::duration
                   ::name
                   ::style
                   ::url
                   ::products
                   :poll/results?
                   :scene/amp
                   :scene/progress-bar]))

(s/def ::scenes (s/coll-of ::scene :kind vector?))

(s/def ::exit
  (s/keys :opt-un [::enabled
                   ::replay]))

(s/def ::related
  (s/keys :req-un [::stories]
          :opt-un [::label]))

(s/def ::google-analytics (s/coll-of string? :kind vector?))

(s/def ::analytics
  (s/keys :opt-un [::google-analytics]))

(s/def :hash/scene
  (s/and keyword?
         (s/or :nil nil?
               :idx #(= :idx %))))

(s/def ::hash
  (s/keys :opt-un [:hash/scene]))

(s/def ::facebook ::url)
(s/def ::twitter ::url)
(s/def ::linkedin ::url)
(s/def ::whatsapp ::url)

(s/def ::og-images
  (s/keys :opt-un [::facebook
                   ::twitter
                   ::linkedin
                   ::whatsapp]))

(s/def ::tracker
  (s/keys :opt-un [::url ::event-name]))

(s/def ::trackers
  (s/coll-of ::tracker :kind vector?))

(s/def ::story
  (s/keys :req-un [::scenes]
          :opt-un [::analytics
                   ::autoplay
                   ::background-audio
                   ::exit
                   ::framed
                   ::full-bleed
                   ::full-screen
                   ::hash
                   ::intro-guide
                   ::landscape
                   :story/loop
                   ::og-images
                   ::related
                   :story/id
                   :story/property-id
                   :story/publisher-id
                   ::trackers
                   ::type]))

(s/def :shape/type #{:drawing :ellipse :star :rectangle :segment})

(s/def :shape/stroke-color string?)
(s/def :shape/stroke-width number?)
(s/def :shape/stroke-linejoin string?)
(s/def :shape/fill-color string?)

(s/def :shape/corner-count integer?)
(s/def :shape/angle-offset number?)
(s/def :shape/middle-point? boolean?)
(s/def :shape/middle-point-distance number?)
(s/def :shape/corner-radius number?)
(s/def :shape/segment-width number?)

;;(s/def :shape/point (s/tuple number? number?))
;;(s/def :shape/points (s/coll-of :shape/point :kind vector?))
;;(s/def :shape/closed-path? boolean?)

(s/def :shape.decorator/type #{:none :rounded :small-arrow :big-arrow :circle :square :diamond})
(s/def :shape.decorator/start :shape.decorator/type)
(s/def :shape.decorator/end :shape.decorator/type)

(s/def :story/gdpr (s/or :nil nil? :zero zero? :one #(= 1 %)))
(s/def :story/share-url ::url)
(s/def :story/tag-data string?)
(s/def :story/var (s/or :string string? :vector vector?))

(s/def ::params
  (s/keys :opt-un [:story/gdpr
                   :story/share-url
                   :story/tag-data
                   :story/var]))
